import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LegendCard from "@/components/feature/LegendCard";
import { legendStories } from "@/mocks/stories";
import { faqList, verifyRules } from "@/mocks/site";

const categories = ["真相求证", "旧事档案", "夜间传说"] as const;

const categoryIcons: Record<string, string> = {
  "真相求证": "ri-search-eye-line",
  "旧事档案": "ri-file-history-line",
  "夜间传说": "ri-moon-foggy-line",
};

const categoryDesc: Record<string, string> = {
  "真相求证": "针对广为流传的校园传言，逐条核对时间、人物与地点，给出可追查的结论。",
  "旧事档案": "把年代久远的校园旧事从口耳相传中抢救出来，对照校史与档案还原脉络。",
  "夜间传说": "记录那些在熄灯后口口相传的传说本身——无论真伪，它们都值得被记住。",
};

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "校园爆料 · 校园怪谈是真的吗？学校未解传闻与校园旧事档案求证";
  }, []);

  const featured = legendStories.slice(0, 4);
  const latest = legendStories.slice(0, 6);

  const categoryStory = (cat: string) => legendStories.filter((s) => s.category === cat);

  return (
    <>
      {/* ===== 首屏 Hero ===== */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=a%20very%20dark%20atmospheric%20old%20university%20campus%20path%20at%20midnight%20lined%20with%20ancient%20trees%20and%20a%20lantern%20glow%20ahead%2C%20fog%2C%20single%20warm%20amber%20street%20lamp%2C%20ink%20wash%20illustration%2C%20deep%20brown%20black%20tones%20with%20warm%20golden%20accent%2C%20mysterious%20quiet%20editorial%20atmosphere&width=1920&height=1080&seq=hero-campus-01&orientation=landscape"
            alt="校园爆料：深夜老校区怪谈求证档案馆首屏背景"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-night-900/80 via-night-900/55 to-night-900"></div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-24 md:px-6">
          <div className="max-w-3xl">
            <div className="fade-up mb-6 inline-flex items-center gap-2 rounded-full border border-amberlight-400/30 bg-night-900/60 px-4 py-1.5 text-sm text-amberlight-300 backdrop-blur">
              <i className="ri-goblet-line"></i>
              校园怪谈求证档案馆 · 深夜开馆
            </div>

            <h1 className="serif-head fade-up delay-1 text-4xl font-black leading-tight text-parchment-100 md:text-6xl">
              校园怪谈，
              <span className="text-amberlight-400">是真的吗？</span>
            </h1>

            <p className="fade-up delay-2 mt-6 max-w-2xl text-lg leading-relaxed text-parchment-300/85 md:text-xl">
              我们把每一则「学校未解传闻」拆成可核实的线索，翻校史、对时间、访亲历者。
              从夜间传说到旧事档案，让校园传言的真相，回到它最初那个真实的人与事。
            </p>

            <div className="fade-up delay-3 mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/archive"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-cinnabar-600 px-7 py-3.5 text-parchment-100 transition hover:bg-cinnabar-500"
              >
                翻阅求证档案
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="#verify"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-parchment-200/30 bg-night-900/40 px-7 py-3.5 text-parchment-100 backdrop-blur transition hover:border-amberlight-400/60"
              >
                我们如何求证
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-parchment-300/70">
              <span className="flex items-center gap-2">
                <i className="ri-shield-keyhole-line text-amberlight-400"></i> 匿名化取证
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-file-search-line text-amberlight-400"></i> 校史对照
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-question-answer-line text-amberlight-400"></i> 多方交叉验证
              </span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 pb-6 text-parchment-300/60">
          <i className="ri-arrow-down-line text-2xl float-y block"></i>
        </div>
      </section>

      {/* ===== 精选求证档案 ===== */}
      <section className="relative bg-night-900 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="hand-script text-2xl text-amberlight-400">本期求证</span>
              <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">精选求证档案</h2>
              <p className="mt-2 text-parchment-300/70">校园怪谈是真的吗？从这些已完成求证的故事开始。</p>
            </div>
            <Link
              to="/archive"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-night-500/60 px-4 py-2 text-sm text-parchment-200 transition hover:border-amberlight-400/60 hover:text-amberlight-300"
            >
              查看全部档案 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((s) => (
              <LegendCard key={s.id} story={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 三大栏目 ===== */}
      <section className="relative border-y border-night-700/40 bg-[#191410] py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <span className="hand-script text-2xl text-amberlight-400">档案分档</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">三大栏目的线索仓库</h2>
            <p className="mx-auto mt-3 max-w-2xl text-parchment-300/70">
              学校未解传闻千头万绪，我们把它们分门别类地收进档案，逐一求证。
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {categories.map((cat) => {
              const count = categoryStory(cat).length;
              return (
                <Link
                  key={cat}
                  to={`/archive?cat=${encodeURIComponent(cat)}`}
                  className="group relative flex flex-col overflow-hidden rounded-lg border border-night-600/50 bg-night-850 p-7 transition hover:border-cinnabar-500/50"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-cinnabar-600/20 text-amberlight-400">
                    <i className={`${categoryIcons[cat]} text-2xl`}></i>
                  </span>
                  <h3 className="serif-head mt-5 text-xl font-bold text-parchment-100">{cat}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-parchment-300/70">{categoryDesc[cat]}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-parchment-300/60">{count} 篇档案在档</span>
                    <span className="inline-flex items-center gap-1 text-sm text-amberlight-400">
                      进入 <i className="ri-arrow-right-line transition group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 最新旧事/夜谈 ===== */}
      <section className="relative bg-night-900 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="hand-script text-2xl text-amberlight-400">夜谈与旧事</span>
              <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">夜间传说与旧事档案</h2>
            </div>
            <div className="flex items-center gap-2 text-sm text-parchment-300/60">
              <i className="ri-hotel-line"></i> 熄灯后，故事开始流传
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {latest.map((s) => (
              <LegendCard key={s.id} story={s} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 求证方式 ===== */}
      <section id="verify" className="relative border-t border-night-700/40 bg-[#1a1510] py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-12 max-w-2xl">
            <span className="hand-script text-2xl text-amberlight-400">求证逻辑</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">我们如何求证一个校园怪谈</h2>
            <p className="mt-3 text-parchment-300/70">
              不渲染恐惧，也不轻易否定一段集体记忆。每则档案都经过这四步，再给出分级结论。
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {verifyRules.map((r, i) => (
              <div key={r.step} className="relative rounded-lg border border-night-600/50 bg-night-850 p-6">
                <span className="serif-head absolute right-4 top-4 text-5xl font-black text-night-600">{r.step}</span>
                <span className="relative inline-flex items-center gap-1 text-xs text-cinnabar-400">
                  第 {i + 1} 步
                </span>
                <h3 className="serif-head relative mt-2 text-lg font-bold text-parchment-100">{r.title}</h3>
                <p className="relative mt-2 text-sm leading-relaxed text-parchment-300/70">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 rounded-lg border border-amberlight-400/25 bg-amberlight-400/5 px-6 py-5">
            <i className="ri-sailboat-line text-2xl text-amberlight-400"></i>
            <p className="flex-1 text-sm leading-relaxed text-parchment-200/85">
              求证结论分为四档：<strong className="text-amberlight-300">有据可考</strong>、
              <strong className="text-parchment-200">有历史原型</strong>、
              <strong className="text-cinnabar-300">证据不足存疑</strong>、
              <strong className="text-parchment-300/80">基本可证为误传</strong>。我们坚持：没有证据，就不下结论。
            </p>
          </div>
        </div>
      </section>

      {/* ===== 常见疑问 FAQ ===== */}
      <section className="relative bg-night-900 py-16 md:py-20">
        <div className="mx-auto w-full max-w-3xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <span className="hand-script text-2xl text-amberlight-400">你问的正是我们求证的</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">常见疑问</h2>
          </div>

          <div className="space-y-3">
            {faqList.slice(0, 5).map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={f.q}
                  className="overflow-hidden rounded-lg border border-night-600/50 bg-night-850"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="serif-head text-base font-semibold text-parchment-100">{f.q}</span>
                    <i
                      className={`shrink-0 text-amberlight-400 transition-transform ${open ? "ri-subtract-line" : "ri-add-line"}`}
                    ></i>
                  </button>
                  {open && (
                    <div className="border-t border-night-700/50 px-5 py-4 text-sm leading-relaxed text-parchment-300/80">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/about#faq"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-amberlight-400 transition hover:text-amberlight-300"
            >
              更多疑问与求证说明 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 行动号召 ===== */}
      <section className="relative overflow-hidden border-t border-night-700/40">
        <img
          src="https://readdy.ai/api/search-image?query=a%20dark%20warm%20campus%20dormitory%20building%20windows%20glowing%20softly%20at%20night%2C%20silhouette%20of%20trees%2C%20fog%2C%20deep%20brown%20and%20amber%20ink%20wash%20illustration%2C%20quiet%20storytelling%20atmosphere&width=1920&height=800&seq=cta-campus-02&orientation=landscape"
          alt="校园爆料：把你的校园传说讲给我们听"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-night-900/82"></div>
        <div className="relative z-10 mx-auto w-full max-w-3xl px-4 py-20 text-center md:px-6">
          <h2 className="serif-head text-3xl font-bold text-parchment-100 md:text-4xl">
            你听过的那则校园怪谈，
            <br className="sm:hidden" />
            值得一个求证
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-parchment-300/85">
            把学校未解传闻、校园旧事或你亲历的怪谈线索告诉我们，一起把它收进档案，还原校园传言的真相。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="mailto:editor@xybaoliao.example.com"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-cinnabar-600 px-7 py-3.5 text-parchment-100 transition hover:bg-cinnabar-500"
            >
              投稿你的传说
            </a>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-parchment-200/30 px-7 py-3.5 text-parchment-100 transition hover:border-amberlight-400/60"
            >
              了解求证方式
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}